import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { Alert } from '../../common/Alert'
import { theme } from '../../../theme/theme'
import { InvoiceDetailsForm } from './InvoiceDetailsForm'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { invoiceDetailsSchema } from '../../../forms/schemas/invoice_details'
import { patchSociety } from '../../../api/societies'

interface InvoiceDetails {
  loading: boolean
}

export const InvoiceDetails = ({
  loading,
}: InvoiceDetails): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.changesSaved')
    )
  }
  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.changesNotSavedError')
    )
  }

  const { mutate: mutatePatchSociety, isPending: isPatchingSociety } =
    useMutation({
      mutationFn: patchSociety,
      onSuccess: () => {
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof invoiceDetailsSchema>
  ): Promise<void> => {
    society && mutatePatchSociety({ id: society._id, body: data })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  if (loading || isLoadingSociety) {
    return <SpinnerWrapper />
  }

  return (
    <div className="w-full">
      <div className="lg:w-1/3">
        <p style={theme.textVariants.lead} className="mb-2">
          {translate('invoiceView.details.title')}
        </p>
        <Alert text={translate('invoiceView.details.alert')} />
      </div>
      <div className="mt-4 md:mt-8">
        {society && (
          <InvoiceDetailsForm
            society={society}
            onError={onError}
            onSubmit={onSubmit}
            loading={isPatchingSociety}
          />
        )}
      </div>
    </div>
  )
}
